import { useCallback, useEffect, useState } from "react";
import { signUpload } from "../../../lib/api";

export type PendingUpload = {
  url: string;
  contentType?: string;
  name?: string;
  size?: number;
  /** Temporary blob URL for instant local preview while the file is uploading. */
  localPreviewUrl?: string;
  uploading?: boolean;
};

export function useUploads() {
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Auto-dismiss the upload error after a few seconds so it doesn't linger
  // on screen (e.g. after a rejected file type). A new error resets the timer.
  useEffect(() => {
    if (!uploadError) return;
    const timer = setTimeout(() => setUploadError(null), 5000);
    return () => clearTimeout(timer);
  }, [uploadError]);

  const addUploads = useCallback(async (files: FileList | File[], token: string) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!token || list.length === 0) return;
    setUploading(true);
    setUploadError(null);

    // Create placeholder entries with instant local previews so the user
    // sees the image immediately rather than waiting for the upload.
    const startIndex = await new Promise<number>(resolve => {
      setPendingUploads(prev => {
        resolve(prev.length);
        const placeholders: PendingUpload[] = list.map(file => {
          let contentType = file.type || "";
          const lower = file.name.toLowerCase();
          if (!contentType || contentType === "application/octet-stream") {
            if (/\.(png|jpg|jpeg)$/i.test(lower)) contentType = "image/png";
            else if (/\.gif$/i.test(lower)) contentType = "image/gif";
            else if (/\.webp$/i.test(lower)) contentType = "image/webp";
            else if (/\.mp4$/i.test(lower)) contentType = "video/mp4";
            else if (/\.md$/i.test(lower)) contentType = "text/markdown";
            else if (/\.csv$/i.test(lower)) contentType = "text/csv";
            else if (/\.txt$/i.test(lower)) contentType = "text/plain";
            else if (/\.pdf$/i.test(lower)) contentType = "application/pdf";
            else if (/\.zip$/i.test(lower)) contentType = "application/zip";
          }
          const isImage = contentType.startsWith("image/");
          return {
            url: "",
            contentType,
            name: file.name,
            size: file.size,
            localPreviewUrl: isImage ? URL.createObjectURL(file) : undefined,
            uploading: true,
          };
        });
        return [...prev, ...placeholders];
      });
    });

    try {
      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        const idx = startIndex + i;
        let contentType = file.type || "";
        const lower = file.name.toLowerCase();
        if (!contentType || contentType === "application/octet-stream") {
          if (/\.(png|jpg|jpeg)$/i.test(lower)) contentType = "image/png";
          else if (/\.gif$/i.test(lower)) contentType = "image/gif";
          else if (/\.webp$/i.test(lower)) contentType = "image/webp";
          else if (/\.mp4$/i.test(lower)) contentType = "video/mp4";
          else if (/\.md$/i.test(lower)) contentType = "text/markdown";
          else if (/\.csv$/i.test(lower)) contentType = "text/csv";
          else if (/\.txt$/i.test(lower)) contentType = "text/plain";
          else if (/\.pdf$/i.test(lower)) contentType = "application/pdf";
          else if (/\.zip$/i.test(lower)) contentType = "application/zip";
        }
        const signed = await signUpload({ filename: file.name, contentType: contentType || "application/octet-stream", size: file.size, purpose: "message" }, token);
        await fetch(signed.url, { method: "PUT", headers: signed.headers, body: file });
        // Replace placeholder with real CDN URL and revoke the blob URL.
        setPendingUploads(prev => {
          const next = [...prev];
          const entry = next[idx];
          // Keep localPreviewUrl alive so the thumbnail stays visible until the message is sent
          next[idx] = { url: signed.publicUrl, contentType, name: file.name, size: file.size, localPreviewUrl: entry?.localPreviewUrl };
          return next;
        });
      }
    } catch (err: any) {
      setUploadError(err?.message || "Failed to upload");
      // Remove placeholder entries that failed
      setPendingUploads(prev => prev.filter((_, i) => i < startIndex || !prev[i]?.uploading));
    } finally {
      setUploading(false);
    }
  }, []);

  const removePending = useCallback((idx: number) => {
    setPendingUploads((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clearPending = useCallback(() => {
    setPendingUploads(prev => {
      prev.forEach(a => { if (a.localPreviewUrl) URL.revokeObjectURL(a.localPreviewUrl); });
      return [];
    });
  }, []);

  return {
    pendingUploads,
    uploading,
    uploadError,
    addUploads,
    removePending,
    clearPending,
    setPendingUploads,
    setUploadError,
  };
}
